import type { StyleFunctionProps } from '@chakra-ui/theme-tools';
import { notionVariantBaseStyles } from '@/theme/styles';
import { defineStyleConfig } from '@/ui';

export const Button = defineStyleConfig({
  baseStyle: {
    borderRadius: '8px',
    fontWeight: 'medium',
    lineHeight: 'normal',
    _focus: {
      boxShadow: 'none',
    },
    _hover: {
      _disabled: {
        bg: undefined,
      },
    },
  },
  sizes: {
    md: {
      fontSize: 14,
    },
    // `minW` matches `h` so icon-only buttons stay square and aren't squished
    // below a tappable size by surrounding flex layouts (text buttons exceed it).
    ah: {
      fontSize: 'ah-control',
      fontWeight: 700,
      h: '32px',
      minW: '32px',
      px: '11px',
      borderRadius: 'ah-lg',
    },
    'ah-sm': {
      fontSize: 'ah-control',
      fontWeight: 700,
      h: '28px',
      minW: '28px',
      px: '10px',
      borderRadius: 'ah-md',
    },
    'ah-xs': {
      fontSize: 'ah-label',
      fontWeight: 700,
      h: '24px',
      minW: '24px',
      px: '8px',
      borderRadius: 'ah-md',
    },
  },
  variants: {
    solid: (props: StyleFunctionProps) => {
      if (props.colorScheme === 'dark') {
        return {
          background: 'dark.900',
          _hover: {
            background: 'dark.500',
          },
          _disabled: {
            background: 'dark.500',
          },
        };
      }
      return {};
    },
    secondary: () => {
      const boxShadow = '0 0 5px rgba(0, 0, 0, 0.08)';

      return {
        borderWidth: 1,
        borderColor: 'rgba(0, 0, 0, .1)',
        color: 'dark.500',
        _hover: {
          borderColor: 'rgba(0, 0, 0, .15)',
          boxShadow,
        },
        _focus: {
          borderColor: 'rgba(0, 0, 0, .15)',
          _hover: {
            boxShadow,
          },
        },
        _active: {
          borderColor: 'rgba(0, 0, 0, .15)',
          boxShadow,
        },
        _dark: {
          borderColor: 'rgba(255, 255, 255, .15)',
          color: 'white',
        },
      };
    },
    ghost: (props: StyleFunctionProps) => {
      if (props.colorMode === 'dark') {
        return {
          _hover: {
            bg: 'customgray.evendarker',
          },
          _active: {
            bg: 'customgray.evendarker',
          },
        };
      }
      if (props.colorScheme === 'dark') {
        return {
          color: 'customgray.textdark',
          _hover: {
            color: 'customgray.textdark',
            bg: props.isDisabled ? 'none' : 'gray.100',
          },
          _active: {
            color: 'customgray.textdark',
            bg: props.isDisabled ? 'none' : 'customgray.light',
          },
        };
      }
      return {
        color: 'brand.500',
        _hover: {
          color: 'brand.600',
          bg: 'brand.40',
        },
        _active: {
          color: 'brand.600',
          bg: 'brand.50',
        },
      };
    },
    notion: notionVariantBaseStyles,
    outline: ({ colorScheme }: StyleFunctionProps) => {
      const isBrand = colorScheme === 'brand';
      const isDark = colorScheme === 'dark';

      return {
        borderColor: `${colorScheme}.500`,
        color: `${colorScheme}.500`,
        _hover: {
          bg: isBrand
            ? `${colorScheme}.500`
            : isDark
              ? '#f8f8f8'
              : `${colorScheme}.50`,
          color: isBrand ? 'white' : `${colorScheme}.500`,
          _disabled: {
            color: `${colorScheme}.500`,
          },
        },
        _active: {
          bg: isBrand
            ? `${colorScheme}.500`
            : isDark
              ? '#f8f8f8'
              : `${colorScheme}.50`,
          color: isBrand ? 'white' : `${colorScheme}.500`,
        },
      };
    },
    shadow: (props: StyleFunctionProps) => {
      const disabled = props.isDisabled || props.isLoading;

      const common = {
        borderWidth: '1px',
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderRadius: '6px',
        shadow: '0px 0px 5px 1px rgba(0, 0, 0, 0.12)',
        bg: 'white',
        _hover: !disabled && {
          bg: 'gray.50',
          shadow: '0px 0px 5px 1px rgba(0, 0, 0, 0.22)',
        },
        _active: !disabled && {
          bg: 'gray.100',
          shadow: '0px 0px 5px 1px rgba(0, 0, 0, 0.22)',
        },
        _focus: !disabled && {
          bg: 'gray.50',
          shadow: '0px 0px 5px 1px rgba(0, 0, 0, 0.22)',
        },
      };

      if (props.colorScheme === 'brand') {
        return {
          ...common,
          borderColor: 'brand.100',
          color: 'brand.500',
          _hover: !disabled && {
            ...common._hover,
            borderColor: 'brand.500',
            color: 'white',
            bg: 'brand.500',
          },
          _active: !disabled && {
            ...common._active,
            borderColor: 'brand.600',
            color: 'white',
            bg: 'brand.600',
          },
          _focus: !disabled && {
            ...common._focus,
            borderColor: 'brand.500',
            color: 'white',
            bg: 'brand.500',
          },
        };
      } else if (props.colorScheme === 'green') {
        const active = !disabled && {
          ...common._active,
          borderColor: 'green.600',
          bg: 'green.600',
        };
        return {
          ...common,
          borderColor: 'green.500',
          color: 'white',
          bg: 'green.500',
          _hover: active,
          _active: active,
          _focus: active,
        };
      }

      return common;
    },
    shadowSubtle: (props: StyleFunctionProps) => {
      const disabled = props.isDisabled || props.isLoading;

      const common = {
        borderWidth: '1px',
        borderColor: 'rgba(0, 0, 0, 0.06)',
        borderRadius: '6px',
        shadow: '0px 0px 3px 1px rgba(0, 0, 0, 0.06)',
        bg: 'white',
        _hover: !disabled && {
          bg: 'gray.50',
          shadow: '0px 0px 3px 1px rgba(0, 0, 0, 0.10)',
        },
        _active: !disabled && {
          bg: 'gray.100',
          shadow: '0px 0px 5px 1px rgba(0, 0, 0, 0.10)',
        },
        _focus: !disabled && {
          bg: 'gray.50',
          shadow: '0px 0px 5px 1px rgba(0, 0, 0, 0.10)',
        },
      };

      if (props.colorScheme === 'brand') {
        return {
          ...common,
          borderColor: 'brand.50',
          color: 'brand.500',
          _hover: !disabled && {
            ...common._hover,
            borderColor: 'brand.500',
            color: 'white',
            bg: 'brand.500',
          },
          _active: !disabled && {
            ...common._active,
            borderColor: 'brand.600',
            color: 'white',
            bg: 'brand.600',
          },
          _focus: !disabled && {
            ...common._focus,
            borderColor: 'brand.500',
            color: 'white',
            bg: 'brand.500',
          },
        };
      } else if (props.colorScheme === 'green') {
        const active = !disabled && {
          ...common._active,
          borderColor: 'green.600',
          bg: 'green.600',
        };
        return {
          ...common,
          borderColor: 'green.500',
          color: 'white',
          bg: 'green.500',
          _hover: active,
          _active: active,
          _focus: active,
        };
      }

      return common;
    },
    control: (props: StyleFunctionProps) => {
      const disabled = props.isDisabled || props.isLoading;

      return {
        borderWidth: '1.5px',
        borderColor: 'rgb(224, 220, 216)',
        bg: 'white',
        color: 'rgb(26, 26, 26)',
        _hover: !disabled && {
          bg: 'gray.50',
          borderColor: 'rgb(200, 196, 192)',
        },
        _active: !disabled && {
          bg: 'gray.100',
          borderColor: 'rgb(200, 196, 192)',
        },
      };
    },
    // Colors are hardcoded — intentionally NOT reading props.colorScheme so the
    // global withDefaultColorScheme('brand') can't bleed pink into any ah* variant.
    ahPrimary: () => ({
      bg: 'magicPurple.500',
      color: 'white',
      borderRadius: 'ah-lg',
      fontWeight: 700,
      _hover: {
        bg: 'magicPurple.600',
        _disabled: { bg: 'magicPurple.500' },
      },
      _active: { bg: 'magicPurple.700' },
    }),
    // Brand-pink solid CTA — same shape as ahPrimary but on the brand ramp,
    // for primary "add/send" actions that lead with the brand color.
    ahBrand: () => ({
      bg: 'brand.500',
      color: 'white',
      borderRadius: 'ah-lg',
      fontWeight: 700,
      _hover: {
        bg: 'brand.600',
        _disabled: { bg: 'brand.500' },
      },
      _active: { bg: 'brand.700' },
    }),
    // Near-black solid CTA — same shape as ahPrimary/ahBrand on the ink ramp,
    // for panels where every primary action should read the same rather than
    // competing for attention on the brand color.
    ahDark: () => ({
      bg: 'ink.strong',
      color: 'white',
      borderRadius: 'ah-lg',
      fontWeight: 700,
      _hover: {
        bg: 'ink.soft',
        _disabled: { bg: 'ink.strong' },
      },
      _active: { bg: 'ink.strong' },
    }),
    // Active state driven by data-active: <Button variant="ahTab" data-active={isActive || undefined}>
    ahTab: () => ({
      bg: 'transparent',
      color: 'ink.soft',
      borderRadius: 'ah-md',
      fontWeight: 500,
      transition: 'background 0.14s, color 0.14s',
      _hover: { bg: 'rgba(0,0,0,0.04)', color: 'ink.strong' },
      '&[data-active]': {
        bg: 'magicPurple.500',
        color: 'white',
        boxShadow: '0 1px 3px rgba(93,63,246,0.3)',
      },
      '&[data-active]:hover': {
        bg: 'magicPurple.600',
      },
    }),
    // Outlined purple — the secondary purple action (e.g. Share, which sits beside
    // the solid-purple Customize). White bg, purple text+border, fills solid purple
    // on hover.
    ahOutline: () => ({
      bg: 'white',
      border: '1px solid',
      borderColor: 'magicPurple.500',
      borderRadius: 'ah-lg',
      color: 'magicPurple.500',
      fontWeight: 700,
      _active: {
        bg: 'magicPurple.600',
        borderColor: 'magicPurple.600',
        color: 'white',
      },
      _hover: {
        bg: 'magicPurple.500',
        color: 'white',
        _disabled: { bg: 'white' },
      },
    }),
    // Subtle neutral outline — white fill with a hairline neutral border for
    // low-emphasis floating controls (e.g. the source-row hide toggle).
    ahOutlineSubtle: () => ({
      bg: 'white',
      border: '1px solid',
      borderColor: 'line.DEFAULT',
      borderRadius: 'ah-lg',
      color: 'ink.soft',
      fontWeight: 500,
      _hover: { bg: 'line.soft', color: 'ink.strong' },
      _active: { bg: 'line.DEFAULT' },
    }),
    // Secondary outlined — for neutral, low-emphasis actions. Transparent bg with a
    // hairline border, fills with a subtle wash on hover.
    ahSecondary: () => ({
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'line.DEFAULT',
      borderRadius: 'ah-lg',
      color: 'ink.strong',
      fontWeight: 700,
      _active: { bg: 'rgba(0,0,0,0.06)' },
      _hover: {
        bg: 'rgba(0,0,0,0.03)',
        borderColor: 'rgba(0,0,0,0.18)',
        _disabled: { bg: 'transparent' },
      },
    }),
    // Dashed neutral outline — for empty "add" placeholders (e.g. the + Who /
    // + Label pills in the customize modal, before a value is set). Dashed
    // hairline border with muted text; fills with a subtle wash on hover.
    ahDashed: () => ({
      bg: 'transparent',
      border: '1px dashed',
      borderColor: 'line.DEFAULT',
      borderRadius: 'ah-lg',
      color: 'ink.muted',
      fontWeight: 500,
      _hover: {
        bg: 'rgba(0,0,0,0.03)',
        borderColor: 'rgba(0,0,0,0.24)',
        color: 'ink.soft',
      },
      _active: { bg: 'rgba(0,0,0,0.06)' },
    }),
    // Lightweight ghost for icon-only or de-emphasized actions
    ahGhost: () => ({
      bg: 'transparent',
      color: 'ink.soft',
      borderRadius: 'ah-md',
      fontWeight: 500,
      _hover: { bg: 'line.soft', color: 'ink.strong' },
      _active: { bg: 'line.DEFAULT' },
    }),
    // Purple-accented ghost for "add / create" affordances (e.g. Add a view),
    // pairing a Plus leftIcon with magicPurple text and a tinted hover wash.
    ahAccentGhost: () => ({
      bg: 'transparent',
      color: 'magicPurple.600',
      borderRadius: 'ah-md',
      fontWeight: 600,
      _hover: { bg: 'magicPurple.25', color: 'magicPurple.700' },
      _active: { bg: 'magicPurple.50' },
    }),
    // White card with a light purple outline + purple label — the prominent
    // "Add a view" affordance. Softer than ahOutline (which fills solid purple):
    // keeps a white fill on hover with a gentle purple wash and a subtle lift.
    ahAccentOutline: () => ({
      bg: 'white',
      border: '1px solid',
      borderColor: 'magicPurple.100',
      borderRadius: 'ah-lg',
      color: 'magicPurple.600',
      fontWeight: 600,
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      _hover: {
        bg: 'magicPurple.25',
        borderColor: 'magicPurple.200',
        color: 'magicPurple.700',
      },
      _active: { bg: 'magicPurple.50', borderColor: 'magicPurple.200' },
      _focusVisible: {
        borderColor: 'magicPurple.300',
        boxShadow: 'ah-ring-active',
        outline: 'none',
      },
    }),
    magic: (props: StyleFunctionProps) => {
      const disabled = props.isDisabled || props.isLoading;

      return {
        border: 'none',
        outline: 'none',
        position: 'relative',
        zIndex: 1,
        borderRadius: '6px',
        bgGradient: 'linear-gradient(to right, #685EDB, #D5257C)',
        shadow: '0px 0px 7px 0px #D5257C80',
        _before: {
          content: '""',
          position: 'absolute',
          left: '1px',
          right: '1px',
          top: '1px',
          bottom: '1px',
          borderRadius: '5px',
          bg: '#E6E1F4',
          zIndex: -1,
        },
        _hover: !disabled && {
          shadow: '0px 0px 10px 0px #D5257C80',
          _before: {
            bg: '#E6E1F4F0',
          },
        },
      };
    },
  },
});
